"use client";

import { c as _c } from "react/compiler-runtime";
import { useContext, useMemo } from "react";
import type { ApiWave } from "@/generated/models/ApiWave";
import { getTimeAgo, numberWithCommas } from "@/helpers/Helpers";
import WaveHeaderFollow from "./WaveHeaderFollow";
import { AuthContext } from "@/components/auth/Auth";
import WaveHeaderOptions from "./options/WaveHeaderOptions";
import WaveHeaderName from "./name/WaveHeaderName";
import WaveHeaderFollowers from "./WaveHeaderFollowers";
import WaveHeaderDescription from "./WaveHeaderDescription";
import WaveHeaderPinButton from "./WaveHeaderPinButton";
import { ApiWaveType } from "@/generated/models/ObjectSerializer";
import WavePicture from "../WavePicture";
import { Time } from "@/helpers/time";
import WaveNotificationSettings from "../specs/WaveNotificationSettings";
import { canEditWave } from "@/helpers/waves/waves.helpers";
import WaveHeaderPictureEdit from "./picture/WaveHeaderPictureEdit";
export enum WaveHeaderPinnedSide {
  LEFT = "LEFT",
  RIGHT = "RIGHT",
}
interface WaveHeaderProps {
  readonly wave: ApiWave;
  readonly onFollowersClick: () => void;
  readonly useRing?: boolean | undefined;
  readonly useRounded?: boolean | undefined;
  readonly pinnedSide?: WaveHeaderPinnedSide | undefined;
}
export default function WaveHeader(t0) {
  const $ = _c(85);
  const {
    wave,
    onFollowersClick,
    useRing: t1,
    useRounded: t2,
    pinnedSide: t3
  } = t0;
  const useRing = t1 === undefined ? true : t1;
  const useRounded = t2 === undefined ? true : t2;
  const pinnedSide = t3 === undefined ? WaveHeaderPinnedSide.RIGHT : t3;
  const {
    connectedProfile,
    activeProfileProxy
  } = useContext(AuthContext);
  let t4;
  if ($[0] !== wave.created_at) {
    t4 = getTimeAgo(wave.created_at);
    $[0] = wave.created_at;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const created = t4;
  let t5;
  if ($[2] !== wave.contributors_overview) {
    t5 = wave.contributors_overview.slice(0, 10);
    $[2] = wave.contributors_overview;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const firstXContributors = t5;
  const isDropWave = wave.wave.type !== ApiWaveType.Chat;
  let t6;
  if ($[4] !== activeProfileProxy || $[5] !== connectedProfile || $[6] !== wave) {
    t6 = canEditWave({
      connectedProfile,
      activeProfileProxy,
      wave
    });
    $[4] = activeProfileProxy;
    $[5] = connectedProfile;
    $[6] = wave;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const canEdit = t6;
  let ringClasses = "";
  if (useRing) {
    ringClasses = "tw-rounded-xl tw-ring-1 tw-ring-inset tw-ring-iron-800/60 tw-shadow-sm";
  } else {
    if (useRounded) {
      ringClasses = "tw-rounded-t-xl lg:tw-rounded-t-none";
    }
  }
  const t7 = `tw-relative tw-overflow-auto tw-bg-iron-950 ${ringClasses}`;
  const t8 = `${useRounded ? "tw-rounded-t-xl tw-ring-1 tw-ring-inset tw-ring-iron-800/70" : ""} tw-overflow-hidden`;
  const t9 = `linear-gradient(60deg, ${wave.author.banner1_color ?? ""} 0%, ${wave.author.banner2_color ?? ""} 100%)`;
  let t10;
  if ($[8] !== t9) {
    t10 = <div className="tw-h-14 tw-w-full tw-object-cover" style={{
      background: t9,
      boxShadow: "inset 0 -4px 12px rgba(0,0,0,0.15)"
    }} />;
    $[8] = t9;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  let t11;
  if ($[10] !== t10 || $[11] !== t8) {
    t11 = <div className={t8}>{t10}</div>;
    $[10] = t10;
    $[11] = t8;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  const t12 = `tw-absolute tw-inset-0 tw-overflow-hidden tw-rounded-full tw-bg-iron-900 tw-shadow-md ${isDropWave ? "tw-ring-2 tw-ring-white/10" : ""}`;
  const t13 = wave.name;
  const t14 = wave.picture;
  let t15;
  if ($[13] !== wave.contributors_overview) {
    t15 = wave.contributors_overview.map(_temp);
    $[13] = wave.contributors_overview;
    $[14] = t15;
  } else {
    t15 = $[14];
  }
  let t16;
  if ($[15] !== t15 || $[16] !== wave.name || $[17] !== wave.picture) {
    t16 = <WavePicture name={t13} picture={t14} contributors={t15} />;
    $[15] = t15;
    $[16] = wave.name;
    $[17] = wave.picture;
    $[18] = t16;
  } else {
    t16 = $[18];
  }
  let t17;
  if ($[19] !== t12 || $[20] !== t16) {
    t17 = <div className={t12}>{t16}</div>;
    $[19] = t12;
    $[20] = t16;
    $[21] = t17;
  } else {
    t17 = $[21];
  }
  let t18;
  if ($[22] !== canEdit || $[23] !== wave) {
    t18 = canEdit && <div className="tw-absolute tw-inset-0"><WaveHeaderPictureEdit wave={wave} /></div>;
    $[22] = canEdit;
    $[23] = wave;
    $[24] = t18;
  } else {
    t18 = $[24];
  }
  let t19;
  if ($[25] !== isDropWave) {
    t19 = isDropWave && <div className="tw-absolute tw-bottom-0 tw-right-0 tw-flex tw-size-6 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-iron-800/50 tw-bg-iron-950 tw-shadow-md"><svg className="tw-size-4 tw-flex-shrink-0 tw-text-[#E8D48A]" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512"><path fill="currentColor" d="M400 0L176 0c-26.5 0-48.1 21.8-47.1 48.2c.2 5.3 .4 10.6 .7 15.8L24 64C10.7 64 0 74.7 0 88c0 92.6 33.5 157 78.5 200.7c44.3 43.1 98.3 64.8 138.1 75.8c23.4 6.5 39.4 26 39.4 45.6c0 20.9-17 37.9-37.9 37.9L192 448c-17.7 0-32 14.3-32 32s14.3 32 32 32l192 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-26.1 0C337 448 320 431 320 410.1c0-19.6 15.9-39.2 39.4-45.6c39.9-11 93.9-32.7 138.2-75.8C542.5 245 576 180.6 576 88c0-13.3-10.7-24-24-24L446.4 64c.3-5.2 .5-10.4 .7-15.8C448.1 21.8 426.5 0 400 0zM48.9 112l84.4 0c9.1 90.1 29.2 150.3 51.9 190.6c-24.9-11-50.8-26.5-73.2-48.3c-32-31.1-58-76-63-142.3zM464.1 254.3c-22.4 21.8-48.3 37.3-73.2 48.3c22.7-40.3 42.8-100.5 51.9-190.6l84.4 0c-5.1 66.3-31.1 111.2-63 142.3z" /></svg></div>;
    $[25] = isDropWave;
    $[26] = t19;
  } else {
    t19 = $[26];
  }
  let t20;
  if ($[27] !== t17 || $[28] !== t18 || $[29] !== t19) {
    t20 = <div className="tw-flex"><div className="tw-group tw-relative tw-size-20">{t17}{t18}{t19}</div></div>;
    $[27] = t17;
    $[28] = t18;
    $[29] = t19;
    $[30] = t20;
  } else {
    t20 = $[30];
  }
  let t21;
  if ($[31] !== activeProfileProxy || $[32] !== connectedProfile?.handle || $[33] !== wave) {
    t21 = !!connectedProfile?.handle && !activeProfileProxy && <div className="tw-inline-flex tw-items-center tw-space-x-2"><WaveNotificationSettings wave={wave} /><WaveHeaderFollow wave={wave} /></div>;
    $[31] = activeProfileProxy;
    $[32] = connectedProfile?.handle;
    $[33] = wave;
    $[34] = t21;
  } else {
    t21 = $[34];
  }
  let t22;
  if ($[35] !== t21) {
    t22 = <div className="tw-mt-10 tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-justify-end tw-gap-x-3 tw-pb-1"><div className="tw-min-w-0 tw-flex-1"><div className="tw-flex tw-flex-col tw-items-end">{t21}</div></div></div>;
    $[35] = t21;
    $[36] = t22;
  } else {
    t22 = $[36];
  }
  let t23;
  if ($[37] !== t20 || $[38] !== t22) {
    t23 = <div className="-tw-mt-6 tw-flex tw-space-x-5 tw-px-4">{t20}{t22}</div>;
    $[37] = t20;
    $[38] = t22;
    $[39] = t23;
  } else {
    t23 = $[39];
  }
  let t24;
  if ($[40] !== wave) {
    t24 = <div className="tw-flex-1"><WaveHeaderName wave={wave} /></div>;
    $[40] = wave;
    $[41] = t24;
  } else {
    t24 = $[41];
  }
  let t25;
  if ($[42] !== activeProfileProxy || $[43] !== connectedProfile?.handle || $[44] !== wave.id) {
    t25 = !!connectedProfile?.handle && !activeProfileProxy && <WaveHeaderPinButton waveId={wave.id} />;
    $[42] = activeProfileProxy;
    $[43] = connectedProfile?.handle;
    $[44] = wave.id;
    $[45] = t25;
  } else {
    t25 = $[45];
  }
  let t26;
  if ($[46] !== activeProfileProxy || $[47] !== connectedProfile || $[48] !== wave) {
    t26 = !!connectedProfile?.handle && !activeProfileProxy && connectedProfile.handle === wave.author.handle && <WaveHeaderOptions wave={wave} />;
    $[46] = activeProfileProxy;
    $[47] = connectedProfile;
    $[48] = wave;
    $[49] = t26;
  } else {
    t26 = $[49];
  }
  let t27;
  if ($[50] !== t25 || $[51] !== t26) {
    t27 = <div className="tw-flex tw-items-center tw-justify-end tw-gap-x-2 tw-self-start">{t25}{t26}</div>;
    $[50] = t25;
    $[51] = t26;
    $[52] = t27;
  } else {
    t27 = $[52];
  }
  let t28;
  if ($[53] !== t24 || $[54] !== t27) {
    t28 = <div className="tw-flex tw-w-full tw-justify-between tw-gap-x-4">{t24}{t27}</div>;
    $[53] = t24;
    $[54] = t27;
    $[55] = t28;
  } else {
    t28 = $[55];
  }
  let t29;
  if ($[56] !== wave.created_at) {
    t29 = Time.millis(wave.created_at).toDate().toLocaleDateString();
    $[56] = wave.created_at;
    $[57] = t29;
  } else {
    t29 = $[57];
  }
  let t30;
  if ($[58] !== created || $[59] !== t29) {
    t30 = <div className="tw-flex-1 tw-text-sm"><span className="tw-font-normal tw-text-iron-400/90">Created {created} ·{" "}{t29}</span></div>;
    $[58] = created;
    $[59] = t29;
    $[60] = t30;
  } else {
    t30 = $[60];
  }
  let t31;
  if ($[61] !== pinnedSide || $[62] !== wave) {
    t31 = <WaveHeaderDescription wave={wave} side={pinnedSide} />;
    $[61] = pinnedSide;
    $[62] = wave;
    $[63] = t31;
  } else {
    t31 = $[63];
  }
  let t32;
  if ($[64] !== t30 || $[65] !== t31) {
    t32 = <div className="tw-mt-2 tw-flex tw-items-center tw-justify-between">{t30}{t31}</div>;
    $[64] = t30;
    $[65] = t31;
    $[66] = t32;
  } else {
    t32 = $[66];
  }
  let t33;
  if ($[67] !== onFollowersClick || $[68] !== wave) {
    t33 = <WaveHeaderFollowers wave={wave} onFollowersClick={onFollowersClick} />;
    $[67] = onFollowersClick;
    $[68] = wave;
    $[69] = t33;
  } else {
    t33 = $[69];
  }
  let t34;
  if ($[70] !== firstXContributors.length || $[71] !== wave.metrics) {
    t34 = !!firstXContributors.length && <div className="tw-flex tw-items-center"><span className="tw-ml-2.5 tw-text-sm tw-font-normal tw-text-iron-400"><span className="tw-pr-0.5 tw-font-medium tw-text-iron-50">{numberWithCommas(wave.metrics.drops_count)}</span>{wave.metrics.drops_count === 1 ? "Post" : "Posts"}</span></div>;
    $[70] = firstXContributors.length;
    $[71] = wave.metrics;
    $[72] = t34;
  } else {
    t34 = $[72];
  }
  let t35;
  if ($[73] !== t33 || $[74] !== t34) {
    t35 = <div className="tw-mt-3 tw-flex tw-flex-col tw-gap-y-3"><div className="tw-flex tw-items-center tw-justify-between tw-gap-x-4"><div className="tw-flex tw-items-center tw-gap-x-4">{t33}{t34}</div></div></div>;
    $[73] = t33;
    $[74] = t34;
    $[75] = t35;
  } else {
    t35 = $[75];
  }
  let t36;
  if ($[76] !== t28 || $[77] !== t32 || $[78] !== t35) {
    t36 = <div className="tw-mt-2 tw-min-w-0 tw-flex-1 tw-px-4 tw-pb-4">{t28}{t32}{t35}</div>;
    $[76] = t28;
    $[77] = t32;
    $[78] = t35;
    $[79] = t36;
  } else {
    t36 = $[79];
  }
  let t37;
  if ($[80] !== t11 || $[81] !== t23 || $[82] !== t36 || $[83] !== t7) {
    t37 = <div className={t7}>{t11}{t23}{t36}</div>;
    $[80] = t11;
    $[81] = t23;
    $[82] = t36;
    $[83] = t7;
    $[84] = t37;
  } else {
    t37 = $[84];
  }
  return t37;
}
function _temp(c) {
  return {
    pfp: c.contributor_pfp
  };
}